import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { Radar, MapPin, Clock, ArrowRight, SatelliteDish } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const REFRESH_MS = 10000;
const BA_FALLBACK = [-34.6037, -58.3816];
const RECENT_MS = 30 * 60 * 1000;

const liveIcon = (color, pulse) => L.divIcon({
  className: "",
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  html: `<div style="width:24px;height:24px;background:${color};border:3px solid #FFFBF4;border-radius:50%;box-shadow:0 0 0 1px ${color};${pulse ? "animation: pulse-green 1.6s infinite;" : ""}"></div>`,
});
const ICON_FRESH = liveIcon("#16a34a", true);
const ICON_STALE = liveIcon("#f59e0b", false);
const ICON_NO_GPS = liveIcon("#6B655A", false);

function timeAgo(iso) {
  if (!iso) return "sin datos";
  const diffSec = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (diffSec < 60) return `hace ${diffSec}s`;
  const mins = Math.floor(diffSec / 60);
  if (mins < 60) return `hace ${mins} min`;
  const hrs = Math.floor(mins / 60);
  return `hace ${hrs} h`;
}

export default function LiveTrackingMap() {
  const location = useLocation();
  const roleBase = location.pathname.startsWith("/supervisor") ? "supervisor" : "admin";
  const [rows, setRows] = useState(null);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("active");

  const load = async () => {
    try {
      const { data } = await api.get("/patrol-tracks/live");
      setRows(data.patrols || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); const i = setInterval(load, REFRESH_MS); return () => clearInterval(i); }, []);

  const isRecent = (r) => r.has_gps && (Date.now() - new Date(r.last_position.recorded_at).getTime()) <= RECENT_MS;

  const activeRows = useMemo(() => (rows || [])
    .filter(isRecent)
    .sort((a, b) => new Date(b.started_at || 0) - new Date(a.started_at || 0)), [rows]);
  const inactiveRows = useMemo(() => (rows || [])
    .filter((r) => !isRecent(r))
    .sort((a, b) => new Date(a.last_position?.recorded_at || a.started_at || 0) - new Date(b.last_position?.recorded_at || b.started_at || 0)), [rows]);
  const visibleRows = tab === "active" ? activeRows : inactiveRows;

  const withGps = useMemo(() => (rows || []).filter((r) => r.last_position), [rows]);
  const center = useMemo(() => {
    if (selected?.last_position) return [selected.last_position.latitude, selected.last_position.longitude];
    if (withGps.length) return [withGps[0].last_position.latitude, withGps[0].last_position.longitude];
    return BA_FALLBACK;
  }, [withGps, selected]);

  const iconFor = (r) => (!r.has_gps ? ICON_NO_GPS : r.stale ? ICON_STALE : ICON_FRESH);
  const statusLabel = (r) => (!r.has_gps ? "Sin señal GPS" : r.stale ? "Sin actualizar hace rato" : "En vivo");
  const statusBadgeClass = (r) => (!r.has_gps ? "gray" : r.stale ? "amber" : "green");

  return (
    <div style={{ padding: "20px 24px 60px" }} data-testid="live-map-page">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 16, marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 11, letterSpacing: "0.2em", color: "#BF3437", fontWeight: 700, textTransform: "uppercase" }}>Operación en vivo</div>
          <h1 style={{ fontSize: 32, fontWeight: 900, margin: "4px 0 0", color: "#0A0A0A", letterSpacing: "-0.02em" }}>Rondas en vivo</h1>
          <div style={{ color: "var(--invict-text-muted)", fontSize: 12, marginTop: 6 }}>
            Seguimiento GPS de vigiladores con una ronda en curso · se actualiza sola cada 10s
          </div>
        </div>
        {rows && (
          <span className="badge green" data-testid="live-map-count">{activeRows.length} ronda(s) en curso</span>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }} className="max-md:!grid-cols-1">
        <div className="card" style={{ padding: 0, overflow: "hidden", height: 520 }} data-testid="live-map-container">
          <MapContainer center={center} zoom={withGps.length ? 14 : 12} style={{ height: "100%", width: "100%" }}>
            <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            {withGps.map((r) => (
              <Marker key={r.patrol_id} position={[r.last_position.latitude, r.last_position.longitude]} icon={iconFor(r)}>
                <Popup>
                  <b>{r.vigilador_name}</b><br/>
                  {r.client_name}{r.site_name ? ` · ${r.site_name}` : ""}<br/>
                  {statusLabel(r)} · {timeAgo(r.last_position.recorded_at)}
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--invict-bone-3)", fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em", color: "#0A0A0A" }}>
            <Radar size={13} style={{ display: "inline", marginRight: 6 }} /> Vigiladores en ronda
          </div>
          <div style={{ display: "flex", borderBottom: "1px solid var(--invict-bone-3)" }}>
            <button
              type="button"
              data-testid="live-map-tab-active"
              onClick={() => setTab("active")}
              style={{
                flex: 1, padding: "10px 12px", fontSize: 12, fontWeight: 700, border: "none", cursor: "pointer",
                background: tab === "active" ? "var(--invict-bone-2)" : "transparent",
                color: tab === "active" ? "#BF3437" : "var(--invict-text-muted)",
                borderBottom: tab === "active" ? "2px solid #BF3437" : "2px solid transparent",
              }}
            >
              En curso ahora ({activeRows.length})
            </button>
            <button
              type="button"
              data-testid="live-map-tab-stale"
              onClick={() => setTab("stale")}
              style={{
                flex: 1, padding: "10px 12px", fontSize: 12, fontWeight: 700, border: "none", cursor: "pointer",
                background: tab === "stale" ? "var(--invict-bone-2)" : "transparent",
                color: tab === "stale" ? "#BF3437" : "var(--invict-text-muted)",
                borderBottom: tab === "stale" ? "2px solid #BF3437" : "2px solid transparent",
              }}
            >
              Sin actividad reciente ({inactiveRows.length})
            </button>
          </div>
          <div style={{ maxHeight: 460, overflowY: "auto" }}>
            {rows == null && <div style={{ padding: 20 }}><div className="skel" style={{ height: 60 }} /></div>}
            {rows && visibleRows.length === 0 && (
              <div data-testid="live-map-empty" style={{ padding: 32, textAlign: "center", color: "var(--invict-text-muted)", fontSize: 13 }}>
                {tab === "active" ? "No hay rondas en curso en este momento." : "No hay rondas sin actividad reciente."}
              </div>
            )}
            {rows && visibleRows.map((r) => (
              <div
                key={r.patrol_id}
                onClick={() => setSelected(r)}
                data-testid={`live-map-row-${r.patrol_id}`}
                style={{
                  display: "block", width: "100%", textAlign: "left", padding: "12px 16px",
                  borderBottom: "1px solid var(--invict-bone-3)", background: selected?.patrol_id === r.patrol_id ? "var(--invict-bone-2)" : "transparent",
                  cursor: "pointer",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                  <div style={{ fontWeight: 800, fontSize: 13, color: "#0A0A0A" }}>{r.vigilador_name}</div>
                  <span className={`badge ${statusBadgeClass(r)}`} data-testid={`live-map-badge-${r.patrol_id}`}>{statusLabel(r)}</span>
                </div>
                <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginTop: 2 }}>
                  <MapPin size={10} style={{ display: "inline", marginRight: 3 }} />{r.client_name}{r.site_name ? ` · ${r.site_name}` : ""}
                </div>
                <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginTop: 2, display: "flex", justifyContent: "space-between" }}>
                  <span><Clock size={10} style={{ display: "inline", marginRight: 3 }} />{r.has_gps ? timeAgo(r.last_position.recorded_at) : "sin posición reportada"}</span>
                  <span>{r.points_completed}/{r.total_points} puntos</span>
                </div>
                <Link to={`/${roleBase}/rondas/${r.patrol_id}`} onClick={(e) => e.stopPropagation()}
                  style={{ fontSize: 10, color: "#BF3437", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4, marginTop: 6, textDecoration: "none" }}>
                  Ver ronda <ArrowRight size={10} />
                </Link>
              </div>
            ))}
          </div>
          <div style={{ padding: "10px 16px", borderTop: "1px solid var(--invict-bone-3)", fontSize: 10, color: "var(--invict-text-muted)", display: "flex", alignItems: "center", gap: 6 }}>
            <SatelliteDish size={11} /> Verde = en vivo · Ámbar = sin actualizar hace rato · Gris = sin señal GPS
          </div>
        </div>
      </div>
    </div>
  );
}
